import React, { useState, useEffect } from "react";
import { db } from "../../firebaseConfig";
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  Timestamp,
  doc,
  updateDoc,
} from "firebase/firestore";

const SuperAdminDashboard = () => {
  const [alerts, setAlerts] = useState([]);
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    totalRevenue: 0,
    totalInvestments: 0,
    vehicles: 0,
    averageROI: 0,
  });
  const [feedback, setFeedback] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [cars, setCars] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [fleetRequests, setFleetRequests] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch Alerts
        const alertsQuery = query(
          collection(db, "alerts"),
          orderBy("timestamp", "desc")
        );
        const alertsSnapshot = await getDocs(alertsQuery);
        setAlerts(alertsSnapshot.docs.map((doc) => doc.data()).slice(0, 5));

        // Fetch Metrics
        const usersSnapshot = await getDocs(collection(db, "users"));
        const carsSnapshot = await getDocs(collection(db, "cars"));
        const totalUsers = usersSnapshot.size;
        let totalRevenue = 0,
          totalInvestments = 0,
          roiSum = 0,
          carCount = 0;

        carsSnapshot.forEach((doc) => {
          const car = doc.data();
          totalRevenue += car.totalRevenue || 0;
          totalInvestments += car.moneyRaised || 0;
          if (car.averageMonthlyROI) {
            roiSum += car.averageMonthlyROI;
            carCount++;
          }
        });

        setMetrics({
          totalUsers,
          totalRevenue,
          totalInvestments,
          vehicles: carsSnapshot.size,
          averageROI: carCount ? (roiSum / carCount).toFixed(2) : 0,
        });

        // Fetch Feedback
        const feedbackSnapshot = await getDocs(
          query(collection(db, "feedback"), orderBy("time", "desc"))
        );
        setFeedback(feedbackSnapshot.docs.map((doc) => doc.data()));

        // Fetch Pending Approvals
        const approvalsSnapshot = await getDocs(
          query(collection(db, "listings"), where("status", "==", "Pending Approval"))
        );
        setPendingApprovals(
          approvalsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        );

        // Fetch Cars
        setCars(carsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));

        // Fetch Investments
        const investmentsSnapshot = await getDocs(collection(db, "investments"));
        setInvestments(investmentsSnapshot.docs.map((doc) => doc.data()));

        // Fetch Payments
        const paymentsSnapshot = await getDocs(collection(db, "payments"));
        setPayments(paymentsSnapshot.docs.map((doc) => doc.data()));

        // Fetch Fleet Requests
        const fleetRequestsSnapshot = await getDocs(
          query(collection(db, "fleetRequests"), orderBy("time", "desc"))
        );
        setFleetRequests(
          fleetRequestsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        );
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const approveListing = async (id) => {
    try {
      const listingRef = doc(db, "listings", id);
      await updateDoc(listingRef, { status: "Approved" });
      setPendingApprovals((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Error approving listing:", error);
    }
  };

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "Arial, sans-serif",
        backgroundColor: "#1e1e1e",
        color: "#ffffff",
        minHeight: "100vh",
      }}
    >
      <h1 style={{ textAlign: "center", marginBottom: "20px" }}>
        Super Admin Dashboard
      </h1>

      {/* Alerts Section */}
      <section style={{ marginBottom: "20px" }}>
       
        <ul style={{ padding: 0, listStyle: "none" }}>
          {alerts.map((alert, index) => (
            <li
              key={index}
              style={{
                marginBottom: "10px",
                padding: "10px",
                background: "#333333",
                borderRadius: "5px",
              }}
            >
              <strong>{alert.title}</strong>: {alert.message} -{" "}
              {new Date(alert.timestamp.seconds * 1000).toLocaleString()}
            </li>
          ))}
        </ul>
      </section>

      {/* Metrics Section */}
      <section style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
        {Object.entries(metrics).map(([key, value]) => (
          <div
            key={key}
            style={{
              flex: "1",
              padding: "20px",
              background: "#333333",
              borderRadius: "5px",
            }}
          >
            <h3 style={{ color: "#4caf50" }}>{key.replace(/([A-Z])/g, " $1")}</h3>
            <p style={{ fontSize: "1.5em", fontWeight: "bold" }}>
              {typeof value === "number" ? value.toLocaleString() : value}
            </p>
          </div>
        ))}
      </section>

      {/* Pending Approvals */}
      <section style={{ marginBottom: "20px" }}>
        <h2 style={{ color: "#4caf50" }}>Pending Approvals</h2>
        <ul>
          {pendingApprovals.map((approval) => (
            <li key={approval.id}>
              {approval.carName} - ${approval.totalValue}{" "}
              <button
                onClick={() => approveListing(approval.id)}
                style={{
                  marginLeft: "10px",
                  padding: "5px 10px",
                  background: "#4caf50",
                  color: "#fff",
                  border: "none",
                  borderRadius: "3px",
                  cursor: "pointer",
                }}
              >
                Approve
              </button>
            </li>
          ))}
        </ul>
      </section>

      {/* Fleet Requests */}
      <section style={{ marginBottom: "20px" }}>
        <h2 style={{ color: "#2196f3" }}>Fleet Requests</h2>
        {fleetRequests.length === 0 ? (
          <p>No fleet requests available.</p>
        ) : (
          <ul>
            {fleetRequests.map((request) => (
              <li key={request.id}>
                <strong>{request.name}</strong>: ${request.investmentAmount} -{" "}
                <span>{request.carTypes}</span> -{" "}
                <a href={`mailto:${request.email}`} style={{ color: "#2196f3" }}>
                  {request.email}
                </a>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Feedback Section */}
      <section style={{ marginBottom: "20px" }}>
        <h2 style={{ color: "#ff9800" }}>User Feedback</h2>
        <ul>
          {feedback.map((fb, index) => (
            <li key={index}>
              <strong>{fb.name}</strong>: {fb.feedback}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default SuperAdminDashboard;
